import { useMemo, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { ADMIN_COLORS } from '../constants/bookingConst';
import { spinnerRing } from '../utils/bookingUtils';
import { getPhToday } from '../utils/phTime';
import {
  getPromos,
  describePromoDates,
  describePromoRate,
  getPromoState,
} from '../utils/promoUtils';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { logAuditEvent, buildPromoSummary, AUDIT_ACTION_TYPES, ENTITY_TYPES } from '../utils/auditLogUtil';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import AdminPromoModal from './AdminPromoModal';
import Modal from './Modal';
import '../css/AdminPromo.css';

const COLORS = ADMIN_COLORS;

// What each state means and how loudly to say it. 'running' is the only one
// that is actually changing prices right now, so it is the only one in the
// club's accent colour; the rest are grey enough to scan past.
const STATE_STYLES = {
  running: { label: 'Running', bg: 'rgba(111, 181, 171, .18)', color: COLORS.cyanDeep },
  scheduled: { label: 'Scheduled', bg: 'rgba(179, 115, 10, .14)', color: COLORS.warning },
  ended: { label: 'Ended', bg: 'rgba(61, 18, 32, .07)', color: COLORS.inkFaint },
  disabled: { label: 'Off', bg: 'rgba(61, 18, 32, .07)', color: COLORS.inkFaint },
};

/** 'yyyy-MM-dd' for today in club time — what "running" is measured against. */
const todayStr = () => {
  const d = getPhToday();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Admin "Promo" screen — limited-time pricing, as a card per promo plus the
 * button that creates one.
 *
 * A promo owns a date range and (optionally) a sport; its RULES narrow the
 * price down to particular courts, weekdays and hours. Once saved, a running
 * promo overrides getCourtRate for the hours it covers, so the guest grid,
 * checkout, receipts and the export all quote the promo price without any of
 * them knowing promos exist — see applyPromoToRate in promoUtils.js.
 *
 * @returns {JSX.Element}
 */
function AdminPromo() {
  const { config, loading: configLoading, updateConfig } = useConfig();
  const { timedOut, restart } = useLoadingTimeout(configLoading);
  // null = closed; an object = the promo being edited; 'new' = create.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalConfig, setModalConfig] = useState({ isOpen: false });

  const showModal = (cfg) => setModalConfig({ isOpen: true, ...cfg });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  const today = todayStr();
  const promos = useMemo(() => getPromos(config), [config]);
  const sportsById = useMemo(() => Object.fromEntries(
    (config?.sports || []).map(s => [s.id, s.name]),
  ), [config?.sports]);

  /**
   * Writes the whole promo list back to config.
   *
   * Whole-list rather than a patch because that is what `config.promos` is —
   * one array — and a partial write would need a second source of truth for
   * ordering.
   *
   * @param {object[]} next
   * @param {string} successMessage
   * @returns {Promise<void>}
   */
  const savePromos = async (next, successMessage, audit = null) => {
    setSaving(true);
    try {
      await updateConfig({ promos: next });
      // Logged only after the write lands: an entry for a save that failed
      // would be a record of something that never happened.
      if (audit) {
        logAuditEvent({
          actionType: audit.actionType,
          entityType: ENTITY_TYPES.PROMO,
          entityId: audit.promo.id,
          statusOld: audit.statusOld ?? null,
          statusNew: audit.statusNew ?? null,
          summary: buildPromoSummary(audit.promo, sportsById[audit.promo.sportId] || null),
        });
      }
      setEditing(null);
      showModal({ title: 'Saved', message: successMessage, type: 'success', confirmText: 'OK', onConfirm: closeModal });
    } catch (error) {
      showModal({
        title: 'Error',
        message: `Failed to save promos: ${error.message}`,
        type: 'error',
        confirmText: 'OK',
      });
    } finally {
      setSaving(false);
    }
  };

  /** @param {object} promo - the finished promo from the editor */
  const handleSavePromo = (promo) => {
    const exists = promos.some(p => p.id === promo.id);
    const next = exists
      ? promos.map(p => (p.id === promo.id ? promo : p))
      : [...promos, promo];
    savePromos(next, exists ? 'Promo updated.' : 'Promo created.', {
      actionType: exists ? AUDIT_ACTION_TYPES.PROMO_UPDATED : AUDIT_ACTION_TYPES.PROMO_CREATED,
      // The promo as SAVED, so the entry shows what the club is running from
      // now on rather than what it was running a moment ago.
      promo,
    });
  };

  /** @param {object} promo */
  const handleDeletePromo = (promo) => {
    showModal({
      title: 'Delete this promo?',
      message: `"${promo.name || 'Untitled promo'}" will stop applying immediately. Bookings already made keep the price they were charged.`,
      type: 'warning',
      confirmText: 'Delete',
      showCancel: true,
      cancelText: 'Keep it',
      onConfirm: () => {
        closeModal();
        savePromos(promos.filter(p => p.id !== promo.id), 'Promo deleted.', {
          actionType: AUDIT_ACTION_TYPES.PROMO_DELETED,
          // Snapshotted here because after this write the promo is gone —
          // the trail is the only place its rates still exist.
          promo,
        });
      },
    });
  };

  /**
   * Turns a promo on or off without deleting it — the seasonal offer that
   * runs again next year should not have to be retyped.
   *
   * @param {object} promo
   */
  const handleToggleEnabled = (promo) => {
    const turningOn = promo.enabled === false;
    savePromos(
      promos.map(p => (p.id === promo.id ? { ...p, enabled: turningOn } : p)),
      turningOn ? 'Promo turned on.' : 'Promo turned off.',
      {
        actionType: turningOn ? AUDIT_ACTION_TYPES.PROMO_ENABLED : AUDIT_ACTION_TYPES.PROMO_DISABLED,
        promo: { ...promo, enabled: turningOn },
        // Reads as "disabled -> enabled" in the trail's Old -> New column,
        // which is the whole content of this action.
        statusOld: turningOn ? 'disabled' : 'enabled',
        statusNew: turningOn ? 'enabled' : 'disabled',
      },
    );
  };

  if (timedOut) {
    return (
      <div className="admin-promo">
        <ConnectionErrorScreen onRetry={restart} />
      </div>
    );
  }

  if (configLoading) {
    return (
      <div className="admin-promo" style={{ padding: '3rem 0', textAlign: 'center' }}>
        {spinnerRing('Loading promos…')}
      </div>
    );
  }

  return (
    <div className="admin-promo">
      <div className="promo-head">
        <div>
          <h2 className="section-title" style={{ color: COLORS.ink, margin: 0 }}>Promo</h2>
          <p className="promo-sub">
            Limited-time pricing. While a promo is running its rates replace the normal court rates
            for the hours it covers — on the guest grid, at checkout and in the export.
          </p>
        </div>
        <button type="button" className="promo-new-btn" onClick={() => setEditing('new')}>
          + Create promo
        </button>
      </div>

      {promos.length === 0 ? (
        <div className="promo-empty">
          <p>No promos yet.</p>
          <p className="promo-empty-sub">
            Create one to run an opening-week rate, a weekday-morning discount, or a price on one
            court only.
          </p>
        </div>
      ) : (
        <div className="promo-grid">
          {promos.map((promo) => {
            const state = getPromoState(promo, today);
            const style = STATE_STYLES[state];
            const rules = Array.isArray(promo.rules) ? promo.rules : [];
            return (
              <article key={promo.id} className={`promo-card${state === 'running' ? ' running' : ''}`}>
                <header className="promo-card-head">
                  <h3 className="promo-card-name">{promo.name || 'Untitled promo'}</h3>
                  <span className="promo-pill" style={{ background: style.bg, color: style.color }}>
                    {style.label}
                  </span>
                </header>

                <p className="promo-card-dates">{describePromoDates(promo)}</p>
                <p className="promo-card-sport">
                  {promo.sportId ? (sportsById[promo.sportId] || promo.sportId) : 'All sports'}
                </p>

                {promo.description && <p className="promo-card-desc">{promo.description}</p>}

                {/* The rates themselves, which is what an admin opens this
                    screen to check. Capped at three lines so one promo with a
                    dozen rules can't push every other card off the screen. */}
                <ul className="promo-card-rates">
                  {rules.slice(0, 3).map(rule => (
                    <li key={rule.id}>
                      <strong>{describePromoRate(rule, config)}</strong>
                      <span>{describePromoScope(rule, config)}</span>
                    </li>
                  ))}
                  {rules.length > 3 && <li className="promo-more">+{rules.length - 3} more</li>}
                  {rules.length === 0 && <li className="promo-more">No rates set — this promo changes nothing.</li>}
                </ul>

                <footer className="promo-card-actions">
                  <button type="button" onClick={() => setEditing(promo)}>Edit</button>
                  <button type="button" onClick={() => handleToggleEnabled(promo)}>
                    {promo.enabled === false ? 'Turn on' : 'Turn off'}
                  </button>
                  <button type="button" className="danger" onClick={() => handleDeletePromo(promo)}>
                    Delete
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {editing && (
        <AdminPromoModal
          promo={editing === 'new' ? null : editing}
          config={config}
          saving={saving}
          onSave={handleSavePromo}
          onClose={() => setEditing(null)}
        />
      )}

      <Modal {...modalConfig} onClose={closeModal} />
    </div>
  );
}

/**
 * "All courts · Sat, Sun · 6-7AM +2" — the one-line scope of a rule.
 *
 * Every empty dimension is left out rather than spelled as "all", so a rule
 * that narrows one thing reads as that one thing.
 *
 * @param {object} rule
 * @param {object} config
 * @returns {string}
 */
const describePromoScope = (rule, config) => {
  const parts = [];

  const courts = (Array.isArray(rule?.courts) ? rule.courts : []).map(Number).filter(Number.isFinite);
  if (courts.length > 0) {
    const names = courts.map((number) => {
      const record = (config?.courts || []).find(c => Number(c.number) === number);
      return record?.name || `Court ${number}`;
    });
    parts.push(names.join(', '));
  }

  const days = (Array.isArray(rule?.days) ? rule.days : []).map(Number).filter(Number.isFinite);
  if (days.length > 0) {
    const short = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    parts.push(days.sort((a, b) => a - b).map(d => short[d]).join(', '));
  }

  const slots = Array.isArray(rule?.timeSlots) ? rule.timeSlots : [];
  if (slots.length === 1) parts.push(slots[0]);
  else if (slots.length > 1) parts.push(`${slots[0]} +${slots.length - 1}`);

  return parts.length > 0 ? parts.join(' · ') : 'Everything in this promo';
};

export default AdminPromo;
